import React, { useState, useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Cell, LineChart, Line, PieChart, Pie, Legend,
    LabelList
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrophy,
    faExclamationTriangle,
    faChartLine,
    faUsers,
    faBookOpen,
    faExclamationCircle,
    faCheckCircle,
    faClock,
    faBook
} from '@fortawesome/free-solid-svg-icons';

// Import the external CSS file
import styles from './StudentAnalytics.module.css';

/**
 * COLOR CONSTANTS
 * Required for Recharts (as it accepts HEX via props, not CSS classes)
 * These match the CSS variables in StudentAnalytics.module.css
 */
const COLORS = {
    primary: '#3b82f6',
    green: '#34d399',
    yellow: '#fbbf24',
    red: '#f87171',
    purple: '#a78bfa',
    gray: '#64748b',
    grid: '#334155',
    text: '#94a3b8'
};

// Recharts Custom Tooltip Style (Inline required by library prop)
const CHART_TOOLTIP_STYLE = {
    backgroundColor: '#1e293b',
    borderColor: '#334155',
    color: '#f1f5f9',
    borderRadius: '8px'
};

/** ------------------------------------------------------------------
 * DUMMY DATA
 * ------------------------------------------------------------------ */

const schoolEngagementData = [
    { name: 'Dav Public', attempts: 1200 },
    { name: 'St. Xavier', attempts: 980 },
    { name: 'Ryan Intl', attempts: 850 },
    { name: 'DPS Delhi', attempts: 720 },
    { name: 'Vibgyor', attempts: 600 },
];
const schoolPerformanceData = [
    { name: 'St. Xavier', score: 92 },
    { name: 'Dav Public', score: 88 },
    { name: 'Ryan Intl', score: 85 },
    { name: 'DPS Delhi', score: 78 },
    { name: 'Vibgyor', score: 72 },
];
const lowPerformingSchools = [
    { name: 'City High', score: 45 },
    { name: 'Govt Model', score: 42 },
    { name: 'Rural East', score: 38 },
];
const inactiveSchools = [
    { name: 'North Valley', lastActive: '45 days ago' },
    { name: 'Sunny Side', lastActive: '32 days ago' },
    { name: 'Greenfield', lastActive: '60 days ago' },
];

const subjectData = [
    { subject: 'Physics', avgScore: 85, attempts: 320, difficulty: 'High', code: 'PHY' },
    { subject: 'Chemistry', avgScore: 72, attempts: 280, difficulty: 'Medium', code: 'CHEM' },
    { subject: 'Biology', avgScore: 90, attempts: 410, difficulty: 'Low', code: 'BIO' },
    { subject: 'Maths', avgScore: 80, attempts: 200, difficulty: 'Low', code: 'MATHS' },
];

const trendData = [
    { month: 'Jun', score: 65, attempts: 120 },
    { month: 'Jul', score: 68, attempts: 180 },
    { month: 'Aug', score: 72, attempts: 250 },
    { month: 'Sep', score: 70, attempts: 300 },
    { month: 'Oct', score: 75, attempts: 380 },
    { month: 'Nov', score: 78, attempts: 450 },
];

const distributionData = [
    { name: 'Excellent', value: 30, fill: COLORS.green },
    { name: 'Good', value: 45, fill: COLORS.primary },
    { name: 'Average', value: 15, fill: COLORS.yellow },
    { name: 'Poor', value: 10, fill: COLORS.red },
];

const rawModuleData = [
    { id: 1, name: 'Kinematics', avgScore: 78, attempts: 120 },
    { id: 2, name: 'Thermodynamics', avgScore: 65, attempts: 90 },
    { id: 3, name: 'Optics VR', avgScore: 82, attempts: 150 },
    { id: 4, name: 'Organic Chem', avgScore: 70, attempts: 110 },
    { id: 5, name: 'Calculus I', avgScore: 55, attempts: 45 },
    { id: 6, name: 'Plant Cells', avgScore: 92, attempts: 200 },
];

const studentRecentAttempts = [
    { id: 1, module: 'Optics VR Lab', score: 85, date: '2025-11-20' },
    { id: 2, module: 'Kinematics Quiz', score: 92, date: '2025-11-18' },
    { id: 3, module: 'Organic Bonds', score: 68, date: '2025-11-15' },
];

const progressData = [
    { name: 'Physics', completed: 14, total: 45, color: COLORS.primary },
    { name: 'Chemistry', completed: 8, total: 20, color: COLORS.primary },
    { name: 'Biology', completed: 4, total: 45, color: COLORS.primary },
    { name: 'Maths', completed: 2, total: 45, color: COLORS.primary },
];

/** ------------------------------------------------------------------
 * HELPER COMPONENTS
 * ------------------------------------------------------------------ */

interface SectionTitleProps {
    title: string;
    role: string;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ title, role }) => (
    <div className={styles['role-section']}>
        <div className={styles['section-title']}>
            {role} <span className={styles['highlight-text']}>/ {title}</span>
        </div>
    </div>
);

interface KPICardProps {
    title: string;
    value: string;
    icon?: any;
    subtext?: string;
    colorClass?: keyof typeof styles;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, icon, subtext, colorClass }) => {
    return (
        <div className={styles.card}>
            <div className={styles['kpi-content']}>
                <div>
                    <p className={`${styles['kpi-title']} ${colorClass ? styles[colorClass] : ''}`}>{title}</p>
                    <h3 className={`${styles['kpi-value']} ${colorClass ? styles[colorClass] : ''}`}>{value}</h3>
                    {subtext && <p className={styles['kpi-subtext']}>{subtext}</p>}
                </div>
                {icon && (
                    <div className={styles['kpi-icon-wrapper']}>
                        <FontAwesomeIcon icon={icon} className={colorClass ? styles[colorClass] : ''} style={{ fontSize: '24px' }} />
                    </div>
                )}
            </div>
        </div>
    );
};

interface HeatmapCellProps {
    subject: string;
    difficulty: string;
}

const HeatmapCell: React.FC<HeatmapCellProps> = ({ subject, difficulty }) => {
    // Determine class based on difficulty for CSS styling
    const difficultyClass = difficulty.toLowerCase() as keyof typeof styles; // 'high', 'medium', 'low'

    return (
        <div className={`${styles['heatmap-cell']} ${styles[difficultyClass]}`}>
            <h4>{subject}</h4>
            <span>{difficulty} Difficulty</span>
        </div>
    );
};

const ModuleCompletionChart: React.FC<{ data: any[] }> = ({ data }) => {
    const chartData = data.map(p => ({
        ...p,
        completedPct: (p.completed / p.total) * 100,
        remainingPct: Math.max(0, 100 - (p.completed / p.total) * 100),
        label: `${p.completed} / ${p.total}`
    }));

    return (
        <div style={{ width: '100%', height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 25, right: 60, left: 0, bottom: 5 }}
                    barCategoryGap={35}
                    barSize={12}
                >
                    <XAxis type="number" hide domain={[0, 100]} />
                    <YAxis
                        type="category"
                        dataKey="name"
                        hide
                    />
                    <Tooltip
                        cursor={{ fill: 'transparent' }}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        formatter={(value: any, name: any) => {
                            if (name === 'completedPct') {
                                return [`${Number(value).toFixed(1)}%`, 'Completion'];
                            }
                            return null;
                        }}
                        itemSorter={(item) => (item.name === 'completedPct' ? -1 : 1)}
                    />
                    {/* Progress Bar (Fill) */}
                    <Bar
                        dataKey="completedPct"
                        stackId="a"
                        fill={COLORS.primary}
                        radius={[6, 0, 0, 6]}
                    />
                    {/* Background Bar (Track) */}
                    <Bar
                        dataKey="remainingPct"
                        stackId="a"
                        fill="rgba(255, 255, 255, 0.05)"
                        radius={[0, 6, 6, 0]}
                        isAnimationActive={false}
                    >
                        <LabelList
                            dataKey="name"
                            content={(props: any) => {
                                const { x, y, value } = props;
                                // Use x=0 or slightly offset to align with the start of the bar
                                return (
                                    <text x={0} y={y - 12} fill="#e2e8f0" fontSize="13px" fontWeight="600" textAnchor="start">
                                        {value}
                                    </text>
                                );
                            }}
                        />
                        <LabelList
                            dataKey="label"
                            position="right"
                            style={{ fill: '#94a3b8', fontSize: '12px', fontWeight: 600 }}
                            offset={12}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};



const StudentAnalytics = () => (
    <div>
        <h1 className={styles.title}>My Learning Portal</h1>

        <div className={styles['grid-3']}>
            <KPICard title="My Average" value="81%" icon={faChartLine} />
            <KPICard title="Modules Completed" value="30 / 180" icon={faCheckCircle} />
            <KPICard title="Best Subject" value="Physics" icon={faBook} colorClass="text-green" />
            <KPICard title="Needs Focus" value="Organic Chem" icon={faExclamationCircle} colorClass="text-red" />
        </div>

        {/* Unified Analytics Row: Subject Performance & Module Completion */}
        <div className={styles['grid-2']} style={{ marginBottom: '24px' }}>
            {/* Subject Performance */}
            <div className={styles.card}>
                <h3 className={styles['card-header']}>Subject Performance</h3>
                <div style={{ height: '350px', width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={subjectData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} barCategoryGap="20%">
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255, 255, 255, 0.08)" />
                            <XAxis
                                dataKey="subject"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'var(--text-secondary)', fontSize: 13, fontWeight: 500 }}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                                domain={[0, 100]}
                                tickFormatter={(value) => `${value}%`}
                            />
                            <Tooltip
                                cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                                contentStyle={CHART_TOOLTIP_STYLE}
                                itemStyle={{ color: '#fff' }}
                                formatter={(value, name) => [value, 'Avg Score']}
                            />
                            <Bar
                                dataKey="avgScore"
                                radius={[6, 6, 0, 0]}
                            >
                                {subjectData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.avgScore >= 85 ? '#10b981' : entry.avgScore >= 70 ? '#f6d13bff' : '#ef4444'}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Module Completion */}
            <div className={styles.card}>
                <h3 className={styles['card-header']}>Module Completion</h3>
                <ModuleCompletionChart data={progressData} />
            </div>
        </div>

        {/* Level 3 Analytics: Recent Activity (Full Width) */}
        <div className={styles['grid-2']}>
            <div className={`${styles.card} ${styles['col-span-2']}`}>
                <h3 className={styles['card-header']}><FontAwesomeIcon icon={faClock} style={{ marginRight: '8px' }} /> Recent Activity</h3>
                <div className={styles['analytics-table-container']}>
                    <table className={styles['analytics-table']}>
                        <thead>
                            <tr>
                                <th>Module</th>
                                <th>Date</th>
                                <th>Score</th>
                                <th style={{ textAlign: 'right' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {studentRecentAttempts.map((attempt) => (
                                <tr key={attempt.id}>
                                    <td>{attempt.module}</td>
                                    <td style={{ color: COLORS.text, fontSize: '13px' }}>{attempt.date}</td>
                                    <td style={{ fontWeight: 'bold' }} className={attempt.score >= 80 ? styles['text-green'] : attempt.score >= 60 ? styles['text-yellow'] : styles['text-red']}>
                                        {attempt.score}%
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <span className={`${styles['status-badge']} ${styles.completed}`}>Completed</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
);

export default StudentAnalytics;
